import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  RefreshCw,
  Mail,
  User,
  Calendar,
  LogOut,
  Briefcase,
  Inbox,
  Settings as SettingsIcon,
  Save,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const { authHeader, logout, email } = useAuth();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState({ spots_remaining: 7, spots_message: "" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("assessments");
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = authHeader();
      const [assessRes, contactRes, leadsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/assessments`, { headers }),
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/email-leads`, { headers }),
        axios.get(`${API}/settings`),
      ]);
      const byDateDesc = (a, b) => new Date(b.created_at) - new Date(a.created_at);
      setAssessments([...assessRes.data].sort(byDateDesc));
      setContacts([...contactRes.data].sort(byDateDesc));
      setLeads([...leadsRes.data].sort(byDateDesc));
      setSettings({
        spots_remaining: settingsRes.data.spots_remaining ?? 7,
        spots_message: settingsRes.data.spots_message || "Only {n} Spots Remaining This Month",
        promo_end_date: settingsRes.data.promo_end_date
          ? settingsRes.data.promo_end_date.slice(0, 16)
          : "",
        video_url: settingsRes.data.video_url || "",
        video_title: settingsRes.data.video_title || "A MESSAGE FROM CHRIS CORMIER",
        video_thumbnail: settingsRes.data.video_thumbnail || "",
        whatsapp_number: settingsRes.data.whatsapp_number || "16193876711",
      });
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    logout();
    toast.success("Logged out");
    navigate("/admin/login", { replace: true });
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        spots_remaining: parseInt(settings.spots_remaining, 10),
        spots_message: settings.spots_message,
        video_url: settings.video_url || "",
        video_title: settings.video_title || "",
        video_thumbnail: settings.video_thumbnail || "",
        whatsapp_number: settings.whatsapp_number || "",
      };
      if (settings.promo_end_date) {
        payload.promo_end_date = new Date(settings.promo_end_date).toISOString();
      } else {
        payload.promo_end_date = "";
      }
      await axios.put(`${API}/settings`, payload, { headers: authHeader() });
      toast.success("Settings saved — homepage will reflect this immediately");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const tabs = [
    { id: "assessments", label: "ASSESSMENTS", icon: User, count: assessments.length },
    { id: "leads", label: "EMAIL LEADS", icon: Inbox, count: leads.length },
    { id: "contacts", label: "CONTACT MSGS", icon: Mail, count: contacts.length },
    { id: "settings", label: "SETTINGS", icon: SettingsIcon, count: null },
  ];

  return (
    <div className="min-h-screen bg-[#000] pt-28 pb-24" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-4xl text-white mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              Admin Dashboard
            </h1>
            <p className="text-[#666] text-sm">
              Signed in as <span className="text-[#C9A962]">{email}</span>
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={fetchData}
              className="btn-outline-premium px-4 py-2 text-xs rounded flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              REFRESH
            </Button>
            <Button
              onClick={handleLogout}
              className="btn-outline-premium px-4 py-2 text-xs rounded flex items-center gap-2"
              data-testid="logout-btn"
            >
              <LogOut size={14} />
              LOGOUT
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-[#1A1A1A]">
          {tabs.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 pb-4 px-3 text-sm font-bold tracking-wider transition-colors ${
                  active
                    ? "text-[#C9A962] border-b-2 border-[#C9A962]"
                    : "text-[#666] hover:text-white"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={14} />
                {tab.label}
                {tab.count !== null && (
                  <span className="text-[10px] opacity-70">({tab.count})</span>
                )}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-20">
            <RefreshCw size={32} className="animate-spin text-[#C9A962] mx-auto mb-4" />
            <p className="text-[#666]">Loading...</p>
          </div>
        ) : (
          <>
            {activeTab === "assessments" && (
              <AssessmentsList
                assessments={assessments}
                expandedId={expandedId}
                setExpandedId={setExpandedId}
              />
            )}

            {activeTab === "leads" && <LeadsList leads={leads} />}

            {activeTab === "contacts" && <ContactsList contacts={contacts} />}

            {activeTab === "settings" && (
              <SettingsPanel
                settings={settings}
                setSettings={setSettings}
                onSave={saveSettings}
                saving={savingSettings}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const SettingsPanel = ({ settings, setSettings, onSave, saving }) => (
  <div className="max-w-2xl bg-[#080808] border border-[#1A1A1A] p-6 sm:p-10">
    <h3 className="text-white font-bold text-xl mb-2">Urgency Banner</h3>
    <p className="text-[#666] text-sm mb-8">
      Controls the spots-remaining counter at the top of the homepage.
    </p>

    <div className="space-y-5">
      <div>
        <Label className="text-xs text-[#666] tracking-wider mb-2 block">
          SPOTS REMAINING
        </Label>
        <Input
          type="number"
          min="0"
          max="999"
          value={settings.spots_remaining}
          onChange={(e) => setSettings({ ...settings, spots_remaining: e.target.value })}
          className="premium-input rounded py-3 px-4 max-w-[160px]"
          data-testid="settings-spots-input"
        />
      </div>
      <div>
        <Label className="text-xs text-[#666] tracking-wider mb-2 block">
          BANNER MESSAGE
        </Label>
        <Input
          value={settings.spots_message}
          onChange={(e) => setSettings({ ...settings, spots_message: e.target.value })}
          className="premium-input rounded py-3 px-4"
          data-testid="settings-message-input"
        />
        <p className="text-[#555] text-xs mt-2">
          Use <code className="text-[#C9A962]">{"{n}"}</code> as a placeholder for the number.
        </p>
      </div>
      <Button
        onClick={onSave}
        disabled={saving}
        className="bg-[#C9A962] text-black hover:bg-white px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold flex items-center gap-2"
        data-testid="settings-save-btn"
      >
        <Save size={14} />
        {saving ? "SAVING..." : "SAVE CHANGES"}
      </Button>
    </div>

    <div className="mt-12 pt-8 border-t border-[#1A1A1A]">
      <h3 className="text-white font-bold text-xl mb-2">Video Introduction</h3>
      <p className="text-[#666] text-sm mb-6">
        Update the "A Message From Chris Cormier" video shown on the homepage.
      </p>
      <div className="space-y-5">
        <div>
          <Label className="text-xs text-[#666] tracking-wider mb-2 block">
            VIDEO URL (YouTube, Vimeo, or direct MP4)
          </Label>
          <Input
            type="url"
            placeholder="https://www.youtube.com/watch?v=..."
            value={settings.video_url || ""}
            onChange={(e) => setSettings({ ...settings, video_url: e.target.value })}
            className="premium-input rounded py-3 px-4"
            data-testid="settings-video-url"
          />
        </div>
        <div>
          <Label className="text-xs text-[#666] tracking-wider mb-2 block">
            VIDEO TITLE
          </Label>
          <Input
            value={settings.video_title || ""}
            onChange={(e) => setSettings({ ...settings, video_title: e.target.value })}
            className="premium-input rounded py-3 px-4"
            data-testid="settings-video-title"
          />
        </div>
        <div>
          <Label className="text-xs text-[#666] tracking-wider mb-2 block">
            CUSTOM THUMBNAIL URL (optional)
          </Label>
          <Input
            type="url"
            placeholder="Leave empty to use default Chris photo"
            value={settings.video_thumbnail || ""}
            onChange={(e) => setSettings({ ...settings, video_thumbnail: e.target.value })}
            className="premium-input rounded py-3 px-4"
            data-testid="settings-video-thumbnail"
          />
        </div>
      </div>
    </div>

    <div className="mt-12 pt-8 border-t border-[#1A1A1A]">
      <h3 className="text-white font-bold text-xl mb-2">WhatsApp Number</h3>
      <p className="text-[#666] text-sm mb-6">
        International format, no symbols (e.g. <code className="text-[#C9A962]">16193876711</code>).
      </p>
      <Input
        value={settings.whatsapp_number || ""}
        onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
        className="premium-input rounded py-3 px-4 max-w-md"
        data-testid="settings-whatsapp-number"
      />
    </div>

    <div className="mt-12 pt-8 border-t border-[#1A1A1A]">
      <h3 className="text-white font-bold text-xl mb-2">June Promo Countdown</h3>
      <p className="text-[#666] text-sm mb-6">
        Set when the June Special Pricing promotion ends. A live countdown will
        appear on both the homepage and the /programs page until this date.
        Leave empty to hide the countdown.
      </p>
      <Label className="text-xs text-[#666] tracking-wider mb-2 block">
        PROMOTION END DATE & TIME
      </Label>
      <Input
        type="datetime-local"
        value={settings.promo_end_date || ""}
        onChange={(e) => setSettings({ ...settings, promo_end_date: e.target.value })}
        className="premium-input rounded py-3 px-4 max-w-md"
        data-testid="settings-promo-end-input"
      />
      <p className="text-[#555] text-xs mt-2">
        Suggested: June 30, 2026 23:59 (end of month). Click "Save Changes"
        above after editing.
      </p>
    </div>
  </div>
);

const LeadsList = ({ leads }) => {
  if (leads.length === 0) {
    return (
      <div className="text-center py-20 bg-[#080808] border border-[#1A1A1A] rounded">
        <Inbox size={48} className="text-[#333] mx-auto mb-4" />
        <p className="text-[#666]">No email leads yet</p>
      </div>
    );
  }
  return (
    <div className="bg-[#080808] border border-[#1A1A1A] divide-y divide-[#1A1A1A]" data-testid="leads-list">
      <div className="grid grid-cols-12 gap-4 px-6 py-3 text-[10px] text-[#666] tracking-[0.2em] uppercase">
        <span className="col-span-3">First Name</span>
        <span className="col-span-5">Email</span>
        <span className="col-span-2">Source</span>
        <span className="col-span-2 text-right">Captured</span>
      </div>
      {leads.map((l) => (
        <div key={l.id} className="grid grid-cols-12 gap-4 px-6 py-4 text-sm">
          <span className="col-span-3 text-white">{l.first_name}</span>
          <span className="col-span-5 text-[#C9A962]">{l.email}</span>
          <span className="col-span-2 text-white/60 text-xs">{l.source}</span>
          <span className="col-span-2 text-right text-[#666] text-xs">
            {new Date(l.created_at).toLocaleDateString()}
          </span>
        </div>
      ))}
    </div>
  );
};

const ContactsList = ({ contacts }) => {
  if (contacts.length === 0) {
    return (
      <div className="text-center py-20 bg-[#080808] border border-[#1A1A1A] rounded">
        <Mail size={48} className="text-[#333] mx-auto mb-4" />
        <p className="text-[#666]">No contact messages yet</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {contacts.map((c) => (
        <div key={c.id} className="bg-[#080808] border border-[#1A1A1A] rounded p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-white font-bold">{c.name}</h3>
              <p className="text-[#C9A962] text-sm">{c.email}</p>
            </div>
            <p className="text-[#666] text-xs">{new Date(c.created_at).toLocaleString()}</p>
          </div>
          <p className="text-[#999] text-sm font-bold mb-2">{c.subject}</p>
          <p className="text-[#999] text-sm whitespace-pre-wrap">{c.message}</p>
        </div>
      ))}
    </div>
  );
};

const AssessmentsList = ({ assessments, expandedId, setExpandedId }) => {
  if (assessments.length === 0) {
    return (
      <div className="text-center py-20 bg-[#080808] border border-[#1A1A1A] rounded">
        <User size={48} className="text-[#333] mx-auto mb-4" />
        <p className="text-[#666]">No athlete assessments yet</p>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {assessments.map((a) => {
        const isExpanded = expandedId === a.id;
        const fullName = `${a.first_name || ""} ${a.last_name || ""}`.trim() || "Unknown";
        return (
          <div
            key={a.id}
            className="bg-[#080808] border border-[#1A1A1A] rounded p-6 hover:border-[#C9A962]/50 transition-colors"
            data-testid={`assessment-card-${a.id}`}
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6 pb-4 border-b border-[#1A1A1A]">
              <div>
                <h3 className="text-xl text-white font-bold flex items-center gap-2">
                  <User size={20} className="text-[#C9A962]" />
                  {fullName}
                </h3>
                <p className="text-[#888] text-sm flex items-center gap-2 mt-1">
                  <Mail size={14} /> {a.email}
                </p>
                <p className="text-[#888] text-sm mt-1">
                  {a.phone}{a.country ? ` · ${a.country}` : ""}
                </p>
              </div>
              <div className="text-right">
                <span className="px-3 py-1 rounded text-xs font-bold bg-[#C9A962]/20 text-[#C9A962]">
                  {(a.status || "PENDING").toUpperCase()}
                </span>
                <p className="text-[#666] text-xs mt-2 flex items-center gap-1 justify-end">
                  <Calendar size={12} />
                  {new Date(a.created_at).toLocaleString()}
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <Block title="VITALS">
                <Row k="Age" v={a.age} />
                <Row k="Gender" v={a.gender} capitalize />
                <Row k="Height" v={a.height} />
                <Row k="Weight" v={a.weight} />
              </Block>
              <Block title="GOALS">
                <Row k="Primary" v={a.primary_goal} />
                <Row k="Deadline" v={a.goal_deadline} />
              </Block>
              <Block title="TRAINING">
                <Row k="Experience" v={a.training_experience} />
                <Row k="Days/Week" v={a.training_days_per_week} />
              </Block>
              <Block title="NUTRITION & HEALTH">
                <Row k="Plan" v={a.structured_nutrition_plan} />
                <Row k="Allergies" v={a.food_allergies} />
                <Row k="Injuries" v={a.injuries} />
              </Block>
            </div>

            <div className="mt-6 pt-6 border-t border-[#1A1A1A]">
              <p className="text-[#C9A962] text-xs font-bold tracking-wider mb-3 flex items-center gap-2">
                <Briefcase size={14} /> LIFESTYLE
              </p>
              <div className="grid sm:grid-cols-3 gap-4 text-sm mb-4">
                <Row k="Work" v={a.work} />
                <Row k="Travels" v={a.travel_frequently} />
                <Row k="Stress" v={a.stress_level} />
              </div>

              {a.typical_day && (
                <ExpandableBlock
                  label="TYPICAL DAY"
                  text={a.typical_day}
                  expanded={isExpanded}
                  onToggle={() => setExpandedId(isExpanded ? null : a.id)}
                />
              )}

              {a.why_now && (
                <div className="bg-[#0E0E0E] border-l-2 border-[#C9A962] p-4 rounded mt-4">
                  <p className="text-[#666] text-xs tracking-wider mb-2">WHY NOW</p>
                  <p className="text-[#DDD] text-sm whitespace-pre-wrap leading-relaxed">{a.why_now}</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Block = ({ title, children }) => (
  <div>
    <p className="text-[#C9A962] text-xs font-bold tracking-wider mb-3">{title}</p>
    <div className="space-y-2 text-sm">{children}</div>
  </div>
);

const Row = ({ k, v, capitalize }) => (
  <p>
    <span className="text-[#666]">{k}:</span>{" "}
    <span className={`text-white ${capitalize ? "capitalize" : ""}`}>
      {v || v === 0 ? String(v) : "—"}
    </span>
  </p>
);

const ExpandableBlock = ({ label, text, expanded, onToggle }) => (
  <div className="bg-[#111] border-l-2 border-[#C9A962] p-4 rounded">
    <p className="text-[#666] text-xs tracking-wider mb-2">{label}</p>
    <p className={`text-[#DDD] text-sm whitespace-pre-wrap leading-relaxed ${expanded ? "" : "line-clamp-4"}`}>
      {text}
    </p>
    {text.length > 200 && (
      <button onClick={onToggle} className="text-[#C9A962] text-xs tracking-wider mt-2 hover:underline">
        {expanded ? "SHOW LESS" : "SHOW MORE"}
      </button>
    )}
  </div>
);

export default AdminPage;
